import type {Formatter, Parser} from 'shared/value-editors/internal/forms/types';
import type {Validator} from 'svelte-forms';
import type {Readable} from 'svelte/store';
import {get} from 'svelte/store';
import {exists, isDefined} from 'shared/utils/custom-utils';
import type {
    TelephoneNumberPrefix,
    TelephoneNumberValueEditorValidations
} from 'shared/value-editors/internal/editors/telephone-number/types';
import {
    notBlankValidator,
    patternValidator,
    requiredValidator
} from 'shared/value-editors/internal/shared/validators';

export function smsCapableTelephoneNumberParser(): Parser {
    return (viewValue: string | null): string => {
        if (!exists(viewValue)) {
            return '';
        }

        // Just removes all spaces - phone number for backend has to be without them
        return viewValue.replace(/\s+/g, '');
    };
}

export function smsCapableTelephoneNumberFormatter(smsCapableReadable: Readable<boolean>, currentPrefixReadable: Readable<TelephoneNumberPrefix | null>): Formatter {
    return (modelValue: string | null): string => {
        if (!exists(modelValue)) {
            return '';
        }

        if (!get(smsCapableReadable)) {
            return modelValue.replace(/\s+/g, '').trim();
        }

        // Removes all spaces - normalization
        const normalizedValue = modelValue.replace(/\s+/g, '');
        const currentPrefix = get(currentPrefixReadable);

        if (exists(currentPrefix)) {
            // Ensure the normalized value starts with the currentPrefix
            if (normalizedValue.startsWith(currentPrefix.dialCode)) {
                // Remove any spaces in the remaining number
                const remainingNumber = normalizedValue.slice(currentPrefix.dialCode.length);

                // Return the prefix and the remaining number separated by a single space
                return `${currentPrefix.dialCode} ${remainingNumber}`.trim();
            }
        }

        // If no prefix -> just return the normalized value without grouping
        return normalizedValue.trim();
    };
}

export function validatorFactory(validations: TelephoneNumberValueEditorValidations): Validator[] {
    const validators: Validator[] = [];

    if (isDefined(validations.required) && validations.required) {
        validators.push(requiredValidator());
    }

    if (isDefined(validations.notBlank) && validations.notBlank) {
        validators.push(notBlankValidator());
    }

    if (isDefined(validations.pattern)) {
        validators.push(patternValidator(new RegExp(validations.pattern)));
    }

    return validators;
}